import { useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Code2, Table2 } from "lucide-react"
import { CSV_TABLE_ROW_CAP, parseCsv } from "@/lib/csv-parse"
import { testIds } from "@/lib/test-ids"

type CsvDocumentViewerProps = {
  content: string
  documentTitle?: string
  onChange?: (next: string) => void
  readOnly?: boolean
}

/**
 * Table view + editable source for document_type csv.
 * Table is read-only; Source edits call onChange so the document Save bar persists.
 */
export function CsvDocumentViewer({
  content,
  documentTitle,
  onChange,
  readOnly = false,
}: CsvDocumentViewerProps) {
  const [mode, setMode] = useState<"table" | "source">("table")
  const parsed = useMemo(() => parseCsv(content), [content])
  const header = parsed.rows[0] ?? []
  const bodyRows = parsed.rows.slice(1)
  const truncated = bodyRows.length > CSV_TABLE_ROW_CAP
  const visibleRows = truncated ? bodyRows.slice(0, CSV_TABLE_ROW_CAP) : bodyRows
  const colCount = Math.max(header.length, ...visibleRows.map((r) => r.length), 1)

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={testIds.csvDocument.root}>
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <Button
          type="button"
          variant={mode === "table" ? "secondary" : "ghost"}
          size="sm"
          data-testid={testIds.csvDocument.tableMode}
          onClick={() => setMode("table")}
        >
          <Table2 className="mr-1 h-4 w-4" aria-hidden />
          Table
        </Button>
        <Button
          type="button"
          variant={mode === "source" ? "secondary" : "ghost"}
          size="sm"
          data-testid={testIds.csvDocument.sourceMode}
          onClick={() => setMode("source")}
        >
          <Code2 className="mr-1 h-4 w-4" aria-hidden />
          Source
        </Button>
        {documentTitle ? (
          <span className="ml-auto truncate text-xs text-muted-foreground">{documentTitle}</span>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {mode === "source" ? (
          <textarea
            data-testid={testIds.csvDocument.source}
            className="h-full w-full resize-none bg-muted/20 p-4 font-mono text-xs text-foreground outline-none"
            value={content}
            readOnly={readOnly || !onChange}
            spellCheck={false}
            onChange={(e) => onChange?.(e.target.value)}
          />
        ) : parsed.error ? (
          <div
            className="p-4 text-sm text-destructive"
            data-testid={testIds.csvDocument.parseError}
          >
            Could not parse CSV: {parsed.error}. Switch to Source to inspect the raw file.
          </div>
        ) : (
          <div className="h-full overflow-auto">
            {truncated ? (
              <p className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
                Showing first {CSV_TABLE_ROW_CAP.toLocaleString()} of {bodyRows.length.toLocaleString()} rows.
                Open Source for the full file.
              </p>
            ) : null}
            <table
              className="w-max min-w-full border-collapse text-sm"
              data-testid={testIds.csvDocument.table}
            >
              <thead className="sticky top-0 bg-background">
                <tr>
                  {Array.from({ length: colCount }, (_, i) => (
                    <th
                      key={i}
                      className="border-b border-border px-3 py-2 text-left font-medium whitespace-nowrap"
                    >
                      {header[i] ?? ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, ri) => (
                  <tr key={ri} className="odd:bg-muted/20">
                    {Array.from({ length: colCount }, (_, ci) => (
                      <td key={ci} className="border-b border-border px-3 py-1.5 whitespace-nowrap">
                        {row[ci] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
